import { usePermissionsContext } from '../contexts/PermissionsContext';
import type { PermissionResource, PermissionAction } from '../types';

/**
 * Hook for checking user permissions
 *
 * Usage:
 * ```tsx
 * const { hasPermission, hasAnyPermission, canAccessLocation, isAdmin } = usePermissions();
 *
 * // Check specific permission
 * if (hasPermission('products', 'create')) {
 *   // User can create products
 * }
 *
 * // Check if user has any permission for a resource
 * if (hasAnyPermission('inventory')) {
 *   // Show inventory menu item
 * }
 *
 * // Check location access
 * if (canAccessLocation(locationId)) {
 *   // User can access this location
 * }
 * ```
 */
export const usePermissions = () => {
  const context = usePermissionsContext();

  return {
    // Permission checking functions
    hasPermission: context.hasPermission,
    hasAnyPermission: context.hasAnyPermission,
    canAccessLocation: context.canAccessLocation,

    // State
    permissions: context.permissions,
    locationIds: context.locationIds,
    locations: context.locations,
    isLoading: context.isLoading,
    isAdmin: context.isAdmin,

    // Actions
    refreshPermissions: context.refreshPermissions,
  };
};

/**
 * Hook to check if user can view financial data (costs, margins, COGS)
 *
 * Usage:
 * ```tsx
 * const canViewCosts = useCanViewFinancials();
 *
 * // Conditionally render cost columns
 * {canViewCosts && <TableCell>Cost</TableCell>}
 * ```
 */
export const useCanViewFinancials = (): boolean => {
  const { hasPermission, isAdmin } = usePermissions();

  // Admins always have access to financial data
  if (isAdmin) {
    return true;
  }

  // Check for explicit financials:read permission
  return hasPermission('financials', 'read');
};

/**
 * Resource labels for display purposes
 */
export const PERMISSION_RESOURCES: Record<PermissionResource, string> = {
  products: 'Products',
  inventory: 'Inventory',
  sales: 'Sales',
  reservations: 'Reservations',
  staff: 'Staff',
  locations: 'Locations',
  reports: 'Reports',
  settings: 'Settings',
  customers: 'Customers',
  b2b: 'B2B Portal',
  deliveries: 'Deliveries',
  financials: 'Financials',
  cash_drawer: 'Cash Drawer',
  credit: 'Customer Credit',
};

/**
 * Action labels for display purposes
 */
export const PERMISSION_ACTIONS: Record<PermissionAction, string> = {
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
};

/**
 * Helper to check if a route requires specific permissions
 */
export const ROUTE_PERMISSIONS: Record<string, { resource: PermissionResource; action: PermissionAction }[]> = {
  '/app/products': [{ resource: 'products', action: 'read' }],
  '/app/products/create': [{ resource: 'products', action: 'create' }],
  '/app/products/edit': [{ resource: 'products', action: 'update' }],
  '/app/inventory': [{ resource: 'inventory', action: 'read' }],
  '/app/sales': [{ resource: 'sales', action: 'read' }],
  '/app/sales/create': [{ resource: 'sales', action: 'create' }],
  '/app/reservations': [{ resource: 'reservations', action: 'read' }],
  '/app/staff': [{ resource: 'staff', action: 'read' }],
  '/app/staff/create': [{ resource: 'staff', action: 'create' }],
  '/app/staff/edit': [{ resource: 'staff', action: 'update' }],
  '/app/roles': [{ resource: 'staff', action: 'read' }],
  '/app/roles/create': [{ resource: 'staff', action: 'create' }],
  '/app/roles/edit': [{ resource: 'staff', action: 'update' }],
  '/app/locations': [{ resource: 'locations', action: 'read' }],
  '/app/locations/create': [{ resource: 'locations', action: 'create' }],
  '/app/locations/edit': [{ resource: 'locations', action: 'update' }],
  '/app/reports': [{ resource: 'reports', action: 'read' }],
  '/app/settings': [{ resource: 'settings', action: 'read' }],
  '/app/customers': [{ resource: 'customers', action: 'read' }],
  '/app/b2b': [{ resource: 'b2b', action: 'read' }],
  '/app/deliveries': [{ resource: 'deliveries', action: 'read' }],
};

export default usePermissions;
